import { useEffect, useMemo, useState, type ReactNode } from 'react'

import type { ItemSobrante, MaterialDeLaOrden } from '@/lib/supabase/tipos-bd'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'

import { cantidadLegible, leerCantidad } from './useSobrantes'
import estilos from './ElegirSobrantes.module.css'

type Props = {
  materiales: MaterialDeLaOrden[]
  cargando: boolean
  error: string | null
  /**
   * null: todavía no contesta (o marcó «sí» sin decir qué). [] = no sobró
   * nada. Con eso la pantalla decide si deja terminar.
   */
  onCambio: (valor: ItemSobrante[] | null) => void
  /** Lo que va al final cuando sí sobró: la foto, en la del técnico (0312). */
  extra?: ReactNode
}

type Respuesta = 'no' | 'si' | null

/**
 * ¿SOBRÓ MATERIAL? (0310)
 *
 * Johan: «el técnico es el que debe reportar esos materiales que sobran», y
 * decidió que sea obligatorio al terminar. La pregunta va con dos respuestas
 * del mismo peso: «no sobró nada» es tan válido como reportar, y no puede
 * costar más toques. Si sobró, la lista es la de los materiales de la orden
 * —no se escribe a mano— y no deja poner más de lo que llevó.
 *
 * La usa el técnico al terminar y la oficina cuando lo registra por él.
 */
export function ElegirSobrantes({ materiales, cargando, error, onCambio, extra }: Props) {
  const [respuesta, setRespuesta] = useState<Respuesta>(null)
  const [cantidades, setCantidades] = useState<Record<number, string>>({})
  const [buscar, setBuscar] = useState('')

  const sinMateriales = !cargando && !error && materiales.length === 0

  const marcados = useMemo(
    () =>
      materiales
        .map((m) => ({ m, n: leerCantidad(cantidades[m.id_inventario] ?? '') }))
        .filter((x) => x.n !== null && x.n > 0) as { m: MaterialDeLaOrden; n: number }[],
    [materiales, cantidades],
  )
  const pasados = marcados.filter((x) => x.n > x.m.cantidad)

  useEffect(() => {
    if (sinMateriales || respuesta === 'no') {
      onCambio([])
    } else if (respuesta === 'si' && marcados.length > 0 && pasados.length === 0) {
      onCambio(marcados.map((x) => ({ id_inventario: x.m.id_inventario, cantidad: x.n })))
    } else {
      onCambio(null)
    }
    // onCambio cambia en cada pintada del padre; lo que importa es el valor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respuesta, marcados, pasados.length, sinMateriales])

  if (sinMateriales) {
    return (
      <div className={estilos.caja}>
        <p className={estilos.pregunta}>Material sobrante</p>
        <p className={estilos.nota}>Esta orden no llevó materiales: no hay nada que reportar.</p>
      </div>
    )
  }

  const t = buscar.trim().toLowerCase()
  const visibles = t
    ? materiales.filter((m) => m.descripcion.toLowerCase().includes(t))
    : materiales

  return (
    <div className={estilos.caja}>
      <p className={estilos.pregunta}>¿Sobró material?</p>
      <p className={estilos.nota}>Lo que no se usó vuelve a bodega y la oficina lo verifica.</p>

      <div className={estilos.respuestas} role="group" aria-label="¿Sobró material?">
        <button
          type="button"
          className={respuesta === 'no' ? estilos.elegida : undefined}
          aria-pressed={respuesta === 'no'}
          onClick={() => setRespuesta('no')}
        >
          No sobró nada
        </button>
        <button
          type="button"
          className={respuesta === 'si' ? estilos.elegida : undefined}
          aria-pressed={respuesta === 'si'}
          onClick={() => setRespuesta('si')}
        >
          Sí, sobró
        </button>
      </div>

      {respuesta === 'si' && (
        <>
          {cargando && <p className={estilos.nota}>Cargando los materiales de la orden…</p>}
          {error && (
            <p className={estilos.error}>
              No se pudo traer la lista de materiales ({error}). Con señal vuelve a abrir «Terminar».
            </p>
          )}

          {materiales.length > 6 && (
            <input
              type="search"
              className={estilos.buscar}
              placeholder={`Buscar entre ${materiales.length} materiales`}
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              aria-label="Buscar material"
            />
          )}

          <ul className={estilos.lista}>
            {visibles.map((m) => {
              const { codigo, nombre } = partirDescripcion(m.descripcion)
              const texto = cantidades[m.id_inventario] ?? ''
              const n = leerCantidad(texto)
              const pasado = n !== null && n > m.cantidad
              return (
                <li key={m.id_inventario} className={n && n > 0 ? estilos.conCantidad : undefined}>
                  <label className={estilos.renglon}>
                    <span className={estilos.material}>
                      <span className={estilos.nombre}>{nombre}</span>
                      <span className={estilos.llevo}>
                        {codigo && <span className={estilos.codigo}>{codigo}</span>}
                        Llevó {cantidadLegible(m.cantidad)} {m.unidad ?? ''}
                      </span>
                    </span>
                    <input
                      className={`${estilos.cantidad} ${pasado ? estilos.cantidadMal : ''}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={texto}
                      aria-label={`Cuánto sobró de ${nombre}`}
                      aria-invalid={pasado}
                      onChange={(e) =>
                        setCantidades((c) => ({ ...c, [m.id_inventario]: e.target.value }))
                      }
                    />
                  </label>
                  {pasado && (
                    <p className={estilos.error}>
                      Llevó {cantidadLegible(m.cantidad)}: no puede sobrar más que eso.
                    </p>
                  )}
                </li>
              )
            })}
            {visibles.length === 0 && <li className={estilos.nota}>Ningún material con «{buscar}».</li>}
          </ul>

          <p className={marcados.length > 0 && pasados.length === 0 ? estilos.resumen : estilos.falta}>
            {pasados.length > 0
              ? `Revisa ${pasados.length === 1 ? 'un material' : `${pasados.length} materiales`}: no puede sobrar más de lo que llevó.`
              : marcados.length === 0
                ? 'Escribe cuánto sobró de cada material.'
                : `${marcados.length} material${marcados.length === 1 ? '' : 'es'} con sobrante.`}
          </p>
          {extra}
        </>
      )}
    </div>
  )
}
